import React, { useEffect, useMemo, useState } from 'react';

import { isArrayEqual } from 'utilitys/helpers/general';

import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';

import { useProjectTreeStore } from 'store/projectContract.store';

import { useMutationSetTagList } from 'api/mutations/setTagList.mutation';
import { useQueryGetTagList } from 'api/queries/getTagList.query';
import { useQueryGetTagListInit } from 'api/queries/getTagListInit.query';

import SelectWithCreate from 'components/common/SelectWithCreate/SelectWithCreate';
import { useSelectWithCreateHook } from 'components/common/SelectWithCreate/SelectWithCreateHook';

import subProgramClasses from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/SubProgramPreferences/components/subProgramPreferences.module.scss';
import { Button } from 'components/DesignSystem/Button';
import { ConfirmSaveModal } from 'components/common';

import classes from './tagList.module.scss';

export const TagList = () => {
    const { project, contract } = useProjectTreeStore();
    const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
    const [selectedValue, setSelectedValue] = useState<ISelectOption<string>[]>([]);
    const { handleSnackBar } = useCustomSnackBar();
    const { data: tagListDataInit, refetch: tagListInitRefetch } = useQueryGetTagListInit({ projectId: project?.id });
    const { data: tagListData, refetch: tagListRefetch } = useQueryGetTagList({ contractId: contract?.id });
    const mutation = useMutationSetTagList();

    const [actionButtonDisabled, setActionButtonDisabled] = useState<boolean>(true);

    const currentValue = useMemo(() => {
        return tagListData ? tagListData.tagList.map((item) => ({ label: item.name, value: item.name })) : [];
    }, [tagListData]);

    useEffect(() => {
        tagListData && setSelectedValue(tagListData.tagList.map((item) => ({ label: item.name, value: item.name })));
    }, [tagListData]);

    const handleHideModal = () => {
        setActionButtonDisabled(true);
        setShowConfirmModal(false);
        setSelectedValue(currentValue);
    };

    const handleSave = () => {
        setShowConfirmModal(false);
        mutation.mutate(
            {
                contractId: contract?.id,
                tagList: selectedValue.map((item) => ({ name: item.value })),
            },
            {
                onSuccess: () => handleSnackBar('Tags has changed', 'success'),
                onError: (error) => {
                    DEFAULT_ERROR({ error, handleError: handleSnackBar });
                },
                onSettled: () => {
                    tagListRefetch();
                    tagListInitRefetch();
                    setActionButtonDisabled(true);
                },
            },
        );
    };

    const handleTagSelect = (value) => {
        const currentValueWithoutIdProp = currentValue.map((item) => {
            delete item.id;
            return item;
        });

        if (isArrayEqual(value, currentValueWithoutIdProp)) {
            setActionButtonDisabled(true);
        } else {
            setActionButtonDisabled(false);
        }

        setSelectedValue(value);
    };

    const tagsOptions = tagListDataInit
        ? tagListDataInit.tags.map((item) => ({ label: item.name, value: item.name }))
        : [];

    const { inputValue, onInputChange } = useSelectWithCreateHook({});

    return (
        <div data-testid={'tag-list-container'} className={classes.tagListContainer}>
            <span className={subProgramClasses.title}>Tags: </span>
            <div className={classes.container}>
                <SelectWithCreate
                    options={tagsOptions}
                    value={selectedValue}
                    onChange={handleTagSelect}
                    isMulti={true}
                    isClearable={true}
                    inputValue={inputValue}
                    onInputChange={onInputChange}
                />

                <div className={classes.actionButtons}>
                    <Button variant={'secondary'} onClick={handleHideModal}>
                        Cancel
                    </Button>
                    <Button
                        variant={'primary'}
                        onClick={() => setShowConfirmModal(true)}
                        disabled={actionButtonDisabled}
                    >
                        Save
                    </Button>
                </div>
            </div>

            <ConfirmSaveModal
                isShowModal={showConfirmModal}
                onHideModal={handleHideModal}
                onConfirm={handleSave}
                isLoading={mutation.isLoading}
            >
                You have made changes. Would you like to save them?
            </ConfirmSaveModal>
        </div>
    );
};
